import { Milestone } from 'src/@types/graphql'
import { ValidationForEditMilestoneDueDate } from 'src/screens/shared/implementation/details/milestone/MilestoneService'
import { MILESTONE_TYPE } from 'src/shared/constants/milestone-constants'
import { DateUtils } from 'src/shared/utils/DateUtils'

export const getProgramValidationForContract = (
  milestones: Milestone[],
): ValidationForEditMilestoneDueDate | undefined => {
  const notContractMilestones = milestones.filter((item) => item.type !== MILESTONE_TYPE.CONTRACT)

  const firstMilestoneAfterContract = notContractMilestones.length > 0 ? notContractMilestones[0] : undefined

  if (firstMilestoneAfterContract == null) {
    return undefined
  }

  const maxDate = DateUtils.parseDate(DateUtils.parseAndFormatDate(firstMilestoneAfterContract.due_date))

  const message =
    firstMilestoneAfterContract.type === MILESTONE_TYPE.ANNUAL_REPORT
      ? 'validation.program.milestone.contract.due-date.after'
      : 'validation.date.invalid.range.after'

  return {
    message,
    maxDate: maxDate,
  }
}

export const getProgramValidationForAnnualPlanning = (
  milestone: Milestone,
  milestones: Milestone[],
  minContractDate?: Date,
  maxContractDate?: Date,
): ValidationForEditMilestoneDueDate | undefined => {
  const currentMilestoneYearInFocus = milestone.year_in_focus

  const lastIntermediateDiscussion = milestones.find(
    (item) =>
      item.type === MILESTONE_TYPE.INTERMEDIATE_DISCUSSION && item.year_in_focus === currentMilestoneYearInFocus - 1,
  )
  const currentYearAnnualReport = milestones.find(
    (item) => item.type === MILESTONE_TYPE.ANNUAL_REPORT && item.year_in_focus === currentMilestoneYearInFocus,
  )

  const minDate = lastIntermediateDiscussion
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(lastIntermediateDiscussion.due_date))
    : minContractDate
  const maxDate = currentYearAnnualReport
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(currentYearAnnualReport.due_date))
    : maxContractDate

  const message =
    !lastIntermediateDiscussion || !currentYearAnnualReport
      ? 'validation.date.invalid.range'
      : 'validation.program.milestone.annual-planning.due-date.is-not-between'

  if (minDate && maxDate) {
    return {
      message,
      minDate: minDate,
      maxDate: maxDate,
    }
  }
}

export const getProgramValidationForAnnualReport = (
  milestone: Milestone,
  milestones: Milestone[],
  minContractDate?: Date,
  maxContractDate?: Date,
): ValidationForEditMilestoneDueDate | undefined => {
  const currentMilestoneYearInFocus = milestone.year_in_focus

  const currentYearIntermediateDiscussion = milestones.find(
    (item) =>
      item.type === MILESTONE_TYPE.INTERMEDIATE_DISCUSSION && item.year_in_focus === currentMilestoneYearInFocus,
  )
  const nextYearIntermediateDiscussion = milestones.find(
    (item) =>
      item.type === MILESTONE_TYPE.INTERMEDIATE_DISCUSSION && item.year_in_focus === currentMilestoneYearInFocus + 1,
  )

  const minDate = currentYearIntermediateDiscussion
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(currentYearIntermediateDiscussion.due_date))
    : minContractDate
  const maxDate = nextYearIntermediateDiscussion
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(nextYearIntermediateDiscussion.due_date))
    : maxContractDate

  const message =
    !currentYearIntermediateDiscussion || !nextYearIntermediateDiscussion
      ? 'validation.date.invalid.range'
      : 'validation.program.milestone.annual-report.due-date.is-not-between'

  if (minDate && maxDate) {
    return {
      message: message,
      minDate: minDate,
      maxDate: maxDate,
    }
  }
}

export const getProgramValidationForIntermediateDiscussion = (
  milestone: Milestone,
  milestones: Milestone[],
  minContractDate?: Date,
  maxContractDate?: Date,
): ValidationForEditMilestoneDueDate | undefined => {
  const currentMilestoneYearInFocus = milestone.year_in_focus

  const lastYearFinancialReport = milestones.find(
    (item) => item.type === MILESTONE_TYPE.FINANCIAL_REPORT && item.year_in_focus === currentMilestoneYearInFocus - 1,
  )
  const currentYearAnnualReport = milestones.find(
    (item) => item.type === MILESTONE_TYPE.ANNUAL_REPORT && item.year_in_focus === currentMilestoneYearInFocus,
  )

  const minDate = lastYearFinancialReport
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(lastYearFinancialReport.due_date))
    : minContractDate
  const maxDate = currentYearAnnualReport
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(currentYearAnnualReport.due_date))
    : maxContractDate

  const message =
    !lastYearFinancialReport || !currentYearAnnualReport
      ? 'validation.date.invalid.range'
      : 'validation.program.milestone.immediate-discussion.due-date.is-not-between'

  if (minDate && maxDate) {
    return {
      message,
      minDate: minDate,
      maxDate: maxDate,
    }
  }
}

export const getProgramValidationForFinancialReport = (
  milestone: Milestone,
  milestones: Milestone[],
  minContractDate?: Date,
  maxContractDate?: Date,
): ValidationForEditMilestoneDueDate | undefined => {
  const currentMilestoneYearInFocus = milestone.year_in_focus

  const currentYearAnnualReport = milestones.find(
    (item) => item.type === MILESTONE_TYPE.ANNUAL_REPORT && item.year_in_focus === currentMilestoneYearInFocus,
  )
  const nextYearIntermediateDiscussion = milestones.find(
    (item) =>
      item.type === MILESTONE_TYPE.INTERMEDIATE_DISCUSSION && item.year_in_focus === currentMilestoneYearInFocus + 1,
  )

  const minDate = currentYearAnnualReport
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(currentYearAnnualReport.due_date))
    : minContractDate
  const maxDate = nextYearIntermediateDiscussion
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(nextYearIntermediateDiscussion.due_date))
    : maxContractDate

  const message =
    !currentYearAnnualReport || !nextYearIntermediateDiscussion
      ? 'validation.date.invalid.range'
      : 'validation.program.milestone.financial-report.due-date.is-not-between'

  if (minDate && maxDate) {
    return {
      message,
      minDate: minDate,
      maxDate: maxDate,
    }
  }
}

export const getProgramValidationForFinalReport = (
  milestones: Milestone[],
): ValidationForEditMilestoneDueDate | undefined => {
  const notFinalReportMilestone = milestones.filter((item) => item.type !== MILESTONE_TYPE.FINAL_REPORT).reverse()

  const beforeLastMilestone = notFinalReportMilestone.length > 0 ? notFinalReportMilestone[0] : undefined

  if (beforeLastMilestone == null) {
    return undefined
  }

  const minDate = DateUtils.parseDate(DateUtils.parseAndFormatDate(beforeLastMilestone.due_date))

  const message =
    beforeLastMilestone.type === MILESTONE_TYPE.FINANCIAL_REPORT
      ? 'validation.program.milestone.final-report.due-date.before'
      : 'validation.date.invalid.range.before'

  if (minDate) {
    return {
      message,
      minDate: minDate,
    }
  }
}
